import MatchEventSetup from "../planning/MatchEventSetup";
import {attachSetupStats} from "../../utils/eventSetupStore";
// D:\new-react-project\the-ultimate-guide\src\components\core-components\LinkVodDialog.jsx

import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../Security/firebase-config";
import "./LinkVodDialog.css";

/**
 * Links TeamStats screenshots ⇄ Review Matches (map-scoped)
 *
 * Props:
 * - teamId (required)
 * - mode: 'teamstats-to-reviewmatch' | 'reviewmatch-to-teamstats'
 *   (Currently both behave the same: we require selection on BOTH sides and write BOTH directions.)
 * - initialTeamStatsMatchId?: string (when opened from TeamStatsPage)
 * - initialReviewMatch?: { mapId: string, reviewMatchId: string } (when opened from Review/VideoList)
 * - onClose(): void
 */
export default function LinkVodDialog({
  teamId,
  mode = "teamstats-to-reviewmatch",
  initialTeamStatsMatchId = null,
  initialReviewMatch = null,
  onClose = () => {},
}) {
  // TeamStats matches live at: teams/{teamId}/matches
  const [teamStatsMatches, setTeamStatsMatches] = useState([]);

  // Review matches live at: teams/{teamId}/maps/{mapId}/matches
  const [reviewMatchesByMap, setReviewMatchesByMap] = useState({});

  const [selectedTeamStatsMatchIds, setSelectedTeamStatsMatchIds] = useState(
    initialTeamStatsMatchId ? [initialTeamStatsMatchId] : []
  );

  const [selectedReviewMatches, setSelectedReviewMatches] = useState(
    initialReviewMatch ? [initialReviewMatch] : []
  );

  const [filterText, setFilterText] = useState("");
  const [saving, setSaving] = useState(false);

  const [saveError, setSaveError] = useState("");
  const [existingTeamStatsLinks, setExistingTeamStatsLinks] = useState([]);
  const [existingReviewMatchLinks, setExistingReviewMatchLinks] = useState([]);
  const didInitExistingLinksRef = useRef(false);

  // Collapsible "folders"
  const [openTeamStatsMaps, setOpenTeamStatsMaps] = useState({});
  const [openReviewMaps, setOpenReviewMaps] = useState({});

  // ----- Load TeamStats matches (screenshots) -----
  useEffect(() => {
    if (!teamId) return;

    const load = async () => {
      const qMatches = query(
        collection(db, "teams", teamId, "matches"),
        orderBy("matchDate", "desc")
      );
      const snap = await getDocs(qMatches);
      setTeamStatsMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };

    load();
  }, [teamId]);

  // ----- Load Review matches across maps (DYNAMIC MAP LIST) -----
  useEffect(() => {
    if (!teamId) return;

    const load = async () => {
      // ✅ This does NOT require map documents to exist
      const MAPS = [
        "DM", "TDM", "Abyss", "Ascent", "Bind", "Breeze", "Corrode", "Fracture",
        "Haven", "Icebox", "Lotus", "Pearl", "Split", "Summit", "Sunset",
      ];

      const out = {};
      for (const mapId of MAPS) {
        const mRef = collection(db, "teams", teamId, "maps", mapId, "matches");
        const snap = await getDocs(mRef);

        out[mapId] = snap.docs.map((d) => ({
          id: d.id,
          mapId,
          ...d.data(),
        }));
      }

      setReviewMatchesByMap(out);

      // Auto-open initial map folder if provided
      if (initialReviewMatch?.mapId) {
        setOpenReviewMaps((prev) => ({ ...prev, [initialReviewMatch.mapId]: true }));
      }
    };

    load().catch((e) => console.error("[LinkVodDialog] load review matches failed:", e));
  }, [teamId, initialReviewMatch]);

  useEffect(() => {
    if (!teamId) return;
    if (didInitExistingLinksRef.current) return;

    const loadExistingLinks = async () => {
      try {
        // Opened from Team Stats page
        if (initialTeamStatsMatchId) {
          const tsRef = doc(db, "teams", teamId, "matches", initialTeamStatsMatchId);
          const tsSnap = await getDoc(tsRef);

          if (tsSnap.exists()) {
            const tsData = tsSnap.data() || {};
            const linkedReviewMatches = Array.isArray(tsData.linkedReviewMatches)
              ? tsData.linkedReviewMatches
              : [];

            setExistingTeamStatsLinks(linkedReviewMatches);

            if (linkedReviewMatches.length) {
              const first = linkedReviewMatches[0];

              setSelectedReviewMatches([
                {
                  mapId: first.mapId,
                  reviewMatchId: first.reviewMatchId,
                },
              ]);

              setOpenReviewMaps((prev) => ({
                ...prev,
                [first.mapId]: true,
              }));
            }
          }
        }

        // Opened from Review side
        if (initialReviewMatch?.mapId && initialReviewMatch?.reviewMatchId) {
          const rmRef = doc(
            db,
            "teams",
            teamId,
            "maps",
            initialReviewMatch.mapId,
            "matches",
            initialReviewMatch.reviewMatchId
          );
          const rmSnap = await getDoc(rmRef);

          if (rmSnap.exists()) {
            const rmData = rmSnap.data() || {};
            const linkedTeamStats = Array.isArray(rmData.linkedTeamStats)
              ? rmData.linkedTeamStats
              : [];

            setExistingReviewMatchLinks(linkedTeamStats);

            if (linkedTeamStats.length && linkedTeamStats[0]?.teamStatsMatchId) {
              setSelectedTeamStatsMatchIds([linkedTeamStats[0].teamStatsMatchId]);
            }
          }
        }

        didInitExistingLinksRef.current = true;
      } catch (e) {
        console.error("[LinkVodDialog] Failed to load existing links:", e);
      }
    };

    loadExistingLinks();
  }, [teamId, initialTeamStatsMatchId, initialReviewMatch]);

  const flatReviewMatches = useMemo(() => {
    return Object.values(reviewMatchesByMap).flat();
  }, [reviewMatchesByMap]);

  // ----- Filtering -----
  const filteredTeamStatsMatches = useMemo(() => {
    if (!filterText.trim()) return teamStatsMatches;
    const t = filterText.toLowerCase();
    return teamStatsMatches.filter((m) =>
      `${m.map || ""} ${m.opponent || ""} ${m.result || ""} ${m.createdBy || ""} ${m.id || ""}`
        .toLowerCase()
        .includes(t)
    );
  }, [teamStatsMatches, filterText]);

  const filteredReviewMatches = useMemo(() => {
    if (!filterText.trim()) return flatReviewMatches;
    const t = filterText.toLowerCase();
    return flatReviewMatches.filter((rm) =>
      `${rm.title || ""} ${rm.createdBy || ""} ${rm.mapId || ""} ${rm.id || ""}`
        .toLowerCase()
        .includes(t)
    );
  }, [flatReviewMatches, filterText]);

  // ----- Grouping (for collapsible map folders) -----
  const teamStatsByMap = useMemo(() => {
    const out = {};
    for (const m of filteredTeamStatsMatches) {
      const key = (m.map || "Unknown").trim() || "Unknown";
      if (!out[key]) out[key] = [];
      out[key].push(m);
    }
    return Object.fromEntries(
      Object.entries(out).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [filteredTeamStatsMatches]);

  const reviewByMap = useMemo(() => {
    const out = {};
    for (const rm of filteredReviewMatches) {
      const key = (rm.mapId || "Unknown").trim() || "Unknown";
      if (!out[key]) out[key] = [];
      out[key].push(rm);
    }
    return Object.fromEntries(
      Object.entries(out).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [filteredReviewMatches]);

  // ----- Selection toggles -----
  const toggleTeamStatsMatch = (id) => {
    setSelectedTeamStatsMatchIds((prev) =>
      prev[0] === id ? [] : [id]
    );
  };

  const toggleReviewMatch = (mapId, reviewMatchId) => {
    const key = `${mapId}__${reviewMatchId}`;
    const has = selectedReviewMatches.some(
      (x) => `${x.mapId}__${x.reviewMatchId}` === key
    );

    setSelectedReviewMatches(
      has ? [] : [{ mapId, reviewMatchId }]
    );
  };

  const existingReviewMatchKeySet = useMemo(() => {
    return new Set(
      (existingTeamStatsLinks || []).map((x) => `${x.mapId}__${x.reviewMatchId}`)
    );
  }, [existingTeamStatsLinks]);

  const existingTeamStatsIdSet = useMemo(() => {
    return new Set(
      (existingReviewMatchLinks || [])
        .map((x) => x.teamStatsMatchId)
        .filter(Boolean)
    );
  }, [existingReviewMatchLinks]);

  const hasNewSelectionsToLink = useMemo(() => {
    const hasNewReviewMatch = selectedReviewMatches.some(
      (x) => !existingReviewMatchKeySet.has(`${x.mapId}__${x.reviewMatchId}`)
    );

    const hasNewTeamStats = selectedTeamStatsMatchIds.some(
      (id) => !existingTeamStatsIdSet.has(id)
    );

    return hasNewReviewMatch || hasNewTeamStats;
  }, [
    selectedReviewMatches,
    selectedTeamStatsMatchIds,
    existingReviewMatchKeySet,
    existingTeamStatsIdSet,
  ]);

  // ----- Commit links (writes both directions) -----
  async function commitLinks() {
    if (!teamId) return;

    const selectedTeamStatsMatchId = selectedTeamStatsMatchIds[0] || null;
    const selectedReviewMatch = selectedReviewMatches[0] || null;

    if (!selectedTeamStatsMatchId || !selectedReviewMatch) return;

    setSaving(true);
    setSaveError("");

    try {
      const rmRef = doc(db, "teams", teamId, "maps", selectedReviewMatch.mapId, "matches", selectedReviewMatch.reviewMatchId);
      const rmSnap = await getDoc(rmRef);
      if (!rmSnap.exists()) throw new Error("Selected VOD match no longer exists.");
      await attachSetupStats({teamId, key: rmSnap.data().calendarEventId || "", statsId: selectedTeamStatsMatchId,
        expectedMatch: {mapId: selectedReviewMatch.mapId, matchId: selectedReviewMatch.reviewMatchId}});
      setSaving(false);
      onClose?.();
    } catch (e) {
      console.error("Link commit failed", e);
      setSaveError(e?.message || "Failed to save link.");
      setSaving(false);
    }
  }

  return (
    <div
      className="lvd__backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="lvd__sheet" onClick={(e) => e.stopPropagation()}>
        <div className="lvd__header">
          <h3 className="lvd__title">Link Team-Stat Screenshots ⇄ VOD Matches</h3>
          <button type="button" className="lvd__close app-cancel-control" onClick={onClose}>
            ✕
          </button>
        </div>

        {selectedReviewMatches[0] && <MatchEventSetup teamId={teamId} mapId={selectedReviewMatches[0].mapId} matchId={selectedReviewMatches[0].reviewMatchId} disabled={saving}/>}
        <div className="lvd__search">
          <input
            type="text"
            placeholder="Search by title, map, opponent, uploader…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {(existingTeamStatsLinks.length > 0 || existingReviewMatchLinks.length > 0) && (
          <div className="lvd__existingSummary">
            {existingTeamStatsLinks.length > 0 && (
              <p className="lvd__existingText">
                This Team Stats item is already linked to {existingTeamStatsLinks.length} VOD match
                {existingTeamStatsLinks.length === 1 ? "" : "es"}.
              </p>
            )}

            {existingReviewMatchLinks.length > 0 && (
              <p className="lvd__existingText">
                This VOD match is already linked to {existingReviewMatchLinks.length} Team Stats item
                {existingReviewMatchLinks.length === 1 ? "" : "s"}.
              </p>
            )}

            {saveError && <p className="lvd__errorText">{saveError}</p>}
          </div>
        )}

        {saveError &&
          !(existingTeamStatsLinks.length > 0 || existingReviewMatchLinks.length > 0) && (
            <div className="lvd__existingSummary">
              <p className="lvd__errorText">{saveError}</p>
            </div>
          )}

        <div className="lvd__columns">
          {/* LEFT: TeamStats screenshots grouped by map */}
          <div className="lvd__col">
            <div className="lvd__colTitle">Team Stats (Screenshots)</div>

            <div className="lvd__list">
              {Object.entries(teamStatsByMap).map(([mapKey, items]) => {
                const open = openTeamStatsMaps[mapKey] ?? true; // default open
                return (
                  <div key={mapKey} className="lvd__group">
                    <button
                      className="lvd__groupHeader"
                      type="button"
                      onClick={() =>
                        setOpenTeamStatsMaps((prev) => ({
                          ...prev,
                          [mapKey]: !open,
                        }))
                      }
                    >
                      <span>
                        {open ? "▾" : "▸"} {mapKey}
                      </span>
                      <span className="lvd__count">{items.length}</span>
                    </button>

                    {open && (
                      <div className="lvd__groupBody">
                        {items.map((m) => (
                          <label key={m.id} className="lvd__row">
                            <input
                              type="radio"
                              name="link-teamstats"
                              checked={selectedTeamStatsMatchIds.includes(m.id)}
                              onChange={() => toggleTeamStatsMatch(m.id)}
                            />
                            <span className="lvd__rowText">
                              <strong>{m.map || "Map ?"}</strong> vs{" "}
                              {m.opponent || "Unknown"} &nbsp;—&nbsp;
                              {m.score
                                ? `${m.score.us ?? "?"}-${m.score.them ?? "?"}`
                                : "–"}{" "}
                              &nbsp;·&nbsp; {m.createdBy || "anon"}

                              {existingTeamStatsIdSet.has(m.id) && (
                                <span className="lvd__linkedBadge">Already linked</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lvd__divider" />

          {/* RIGHT: Review matches grouped by map */}
          <div className="lvd__col">
            <div className="lvd__colTitle">VOD Matches (Review)</div>

            <div className="lvd__list">
              {Object.entries(reviewByMap).map(([mapKey, items]) => {
                const defaultOpen =
                  mapKey === (initialReviewMatch?.mapId || "");
                const open = openReviewMaps[mapKey] ?? defaultOpen;

                return (
                  <div key={mapKey} className="lvd__group">
                    <button
                      className="lvd__groupHeader"
                      type="button"
                      onClick={() =>
                        setOpenReviewMaps((prev) => ({
                          ...prev,
                          [mapKey]: !open,
                        }))
                      }
                    >
                      <span>
                        {open ? "▾" : "▸"} {mapKey}
                      </span>
                      <span className="lvd__count">{items.length}</span>
                    </button>

                    {open && (
                      <div className="lvd__groupBody">
                        {items.map((rm) => (
                          <label
                            key={`${rm.mapId}__${rm.id}`}
                            className="lvd__row"
                          >
                            <input
                              type="radio"
                              name="link-reviewmatch"
                              checked={selectedReviewMatches.some(
                                (x) =>
                                  x.mapId === rm.mapId &&
                                  x.reviewMatchId === rm.id
                              )}
                              onChange={() => toggleReviewMatch(rm.mapId, rm.id)}
                            />
                            <span className="lvd__rowText">
                              <strong>
                                {rm.title || `Match ${rm.id.slice(0, 8)}`}
                              </strong>
                              &nbsp;·&nbsp; {rm.mapId}
                              &nbsp;·&nbsp; {rm.createdBy || "anon"}

                              {existingReviewMatchKeySet.has(`${rm.mapId}__${rm.id}`) && (
                                <span className="lvd__linkedBadge">Already linked</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lvd__footer">
          <button
            type="button"
            className="lvd__btn lvd__btn--secondary app-cancel-control"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="lvd__btn lvd__btn--primary"
            onClick={commitLinks}
            disabled={
              saving ||
              !selectedTeamStatsMatchIds.length ||
              !selectedReviewMatches.length ||
              !hasNewSelectionsToLink
            }
            title={
              !selectedTeamStatsMatchIds.length || !selectedReviewMatches.length
                ? "Select exactly one screenshot and one review match to link."
                : !hasNewSelectionsToLink
                ? "These selections are already linked."
                : ""
            }
          >
            {saving ? "Linking…" : "Link Selected"}
          </button>
        </div>
      </div>
    </div>
  );
}
